import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import useScrolBarSize from '../../hooks/useScrollbarSize';
import {
  assignColumnAccessor,
  Column,
  IdArrayInput,
  ItemLoader,
} from '../../hooks/useTablePages';
import {
  Table,
  TableCell,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
} from './Table';
import styled from '@emotion/styled';
import { Checkbox } from '../Form';
import useSize from '@react-hook/size';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { ColumnConfig } from '../../hooks/useColumnConfiguration';
import TableActionBar from './TableActionBar';
import { Theme } from '../../theme';
import { OrderBy } from '../../hooks/useOrderBy';
import TableColumn, { TableColumnProps } from './TableColumn';

import './TableWindow.css';
import { Box, Flex, FlexProps } from 'rebass';
import ColumnSelector from './ColumnSelector';
import { RightPaneConfig } from '../../hooks/useRightPaneConfiguration';
import Icon, { IconType } from '../Icon/Icon';

const noop = () => {};

const StyledTable = styled(Table)<{
  maxWidth?: number;
  maxHeight?: number;
  theme: Theme;
}>`
  box-sizing: border-box;
  flex-grow: 1;
  flex-shrink: 1;

  .tbody {
    overflow-x: hidden !important;
    white-space: nowrap;
  }

  input[type='checkbox'] {
    margin: 0;
    padding: 0;
  }

  ${({ maxHeight }) => (maxHeight ? `max-height: ${maxHeight}px` : '')};
  ${({ maxWidth }) => (maxWidth ? `max-width: ${maxWidth}px` : '')};
`;

const StyledTableRow = styled(TableRow)`
  position: absolute;
  width: 100%;
`;

const StyledCheckbox = styled(Checkbox)<{ color?: string }>`
  position: relative;
  margin-top: -2px;
  margin-bottom: -3px;
  margin-left: -2px;
  width: 16px;
  height: 16px;
  ${({ color }) => (color ? `color: ${color} !important` : '')}
`;

type HeaderRowProps<
  Item extends { [Id in IdField]: IdValue } = any,
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
> = {
  headerHeight: number;
  totalColumnsWidth: number;
  scrollBarSize: number;
  allowSelection: boolean;
  selectAllClick?: () => void;
  selectedIds?: IdArrayInput<IdValue>;
  itemIds: IdArrayInput<IdValue>;
  columns: Column[];
  onHeaderClick?: (column: Column) => void;
  orderBy?: OrderBy[];
  checkboxColumnWidth: number;
  columnConfiguration?: ColumnConfig[];
  setColumnConfiguration?: (columns: ColumnConfig[]) => void;
};

const HeaderRow: React.FC<HeaderRowProps> = <
  Item extends { [Id in IdField]: IdValue },
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
>(
  props: HeaderRowProps<Item, IdField, IdValue>
) => {
  const {
    headerHeight,
    totalColumnsWidth,
    scrollBarSize,
    allowSelection,
    selectAllClick,
    selectedIds,
    itemIds,
    columns,
    onHeaderClick,
    checkboxColumnWidth,
    orderBy,
    columnConfiguration,
    setColumnConfiguration,
  } = props;

  const dragEnabled = !!setColumnConfiguration && !!columnConfiguration;

  const reorder = (
    listOfColumns: ColumnConfig[],
    startIndex: number,
    endIndex: number
  ): ColumnConfig[] => {
    const result = Array.from(listOfColumns);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result as ColumnConfig[];
  };

  const onDragEnd = (result: DropResult) => {
    if (
      !setColumnConfiguration ||
      !columnConfiguration ||
      !result.destination ||
      result.destination.index === result.source.index
    ) {
      return;
    }

    const enabledColumns = columnConfiguration.filter(
      (column) => column.enabled
    );

    const hiddenColumns = columnConfiguration.filter(
      (column) => !column.enabled
    );

    const hiddenIndices = hiddenColumns.map((hiddenColumn) => {
      return columnConfiguration.findIndex(
        (column) => column.id === hiddenColumn.id
      );
    });

    const reorderedColumns = reorder(
      enabledColumns,
      result.source.index,
      result.destination.index
    );
    hiddenIndices.forEach((hiddenIdx, idx) =>
      reorderedColumns.splice(hiddenIdx, 0, hiddenColumns[idx])
    );
    setColumnConfiguration(reorderedColumns);
  };

  if (itemIds.length === 0) {
    return (
      <TableHeaderRow
        style={{
          height: headerHeight,
          width: totalColumnsWidth + scrollBarSize,
        }}
        className="table-header-row"
        role="row"
      />
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable
        droppableId="visibleColumns"
        isDropDisabled={!dragEnabled}
        direction="horizontal"
      >
        {(provided, snapshot) => (
          <TableHeaderRow
            style={{
              height: headerHeight,
              width: totalColumnsWidth + scrollBarSize,
            }}
            className="table-header-row"
            role="row"
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {allowSelection && (
              <TableHeaderCell
                role="columnheader"
                onClick={selectAllClick}
                style={{ width: checkboxColumnWidth }}
              >
                <StyledCheckbox
                  color="white"
                  checked={itemIds.length === selectedIds?.length}
                  onChange={noop}
                  title={
                    itemIds.length === selectedIds?.length
                      ? 'Deselect All'
                      : 'Select All'
                  }
                />
              </TableHeaderCell>
            )}
            {columns.map((column, index) => {
              const id = column.id || (column.accessor as 'string');
              const columnOrderBy = orderBy
                ? orderBy.find((value) => value.col === id)
                : undefined;

              return (
                <Draggable
                  key={id ?? index}
                  isDragDisabled={!dragEnabled}
                  draggableId={id}
                  index={index}
                >
                  {(provided, snapshot) => {
                    return (
                      <TableHeaderCell
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          ...provided.draggableProps.style,
                          width: column.width,
                        }}
                        role="columnheader"
                        onClick={() =>
                          column.sortable !== false && onHeaderClick
                            ? onHeaderClick(column)
                            : undefined
                        }
                      >
                        {column.header}
                        {columnOrderBy ? (
                          columnOrderBy.dir === 'ASC' ? (
                            <Icon icon="sort-asc" size="1.3em" />
                          ) : (
                            <Icon icon="sort-desc" size="1.3em" />
                          )
                        ) : (
                          ''
                        )}
                      </TableHeaderCell>
                    );
                  }}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </TableHeaderRow>
        )}
      </Droppable>
    </DragDropContext>
  );
};

HeaderRow.propTypes = {
  headerHeight: PropTypes.any,
  totalColumnsWidth: PropTypes.any,
  allowSelection: PropTypes.any,
  selectAllClick: PropTypes.any,
  selectedIds: PropTypes.any,
  itemIds: PropTypes.any,
  columns: PropTypes.any,
  onHeaderClick: PropTypes.any,
  checkboxColumnWidth: PropTypes.any,
  orderBy: PropTypes.array,
  columnConfiguration: PropTypes.any,
  setColumnConfiguration: PropTypes.any,
};

const ItemRow: React.FC<ListChildComponentProps> = ({ data, index, style }) => {
  const {
    itemIds,
    itemLoader,
    columns,
    selectedIds,
    onSelectionChange,
    rowHeight,
    allowSelection,
    checkboxColumnWidth,
    onRowClick,
    activeIds,
  } = data;
  const item = itemLoader.useItem(index);
  const itemId = itemIds[index];
  const selectedIndex = selectedIds ? selectedIds.indexOf(itemId) : -1;
  const selected = selectedIndex !== -1;
  const onClick = useCallback(
    (e) => {
      if (
        onRowClick &&
        !(e as any).target.matches(
          'button, .checkbox-cell, .checkbox-cell *, input, a, button *, a *'
        )
      ) {
        onRowClick(item, index);
      }
    },
    [onRowClick, item]
  );
  const selectRowClick = useCallback(
    (e) => {
      if (onSelectionChange) {
        const newSelectedIds = selectedIds?.slice() || [];
        if (selected) {
          newSelectedIds.splice(selectedIndex, 1);
        } else {
          newSelectedIds.push(itemId);
        }
        onSelectionChange(newSelectedIds);
      }
    },
    [onSelectionChange, selected, selectedIds]
  );

  return (
    <StyledTableRow
      key={'item-' + itemId}
      style={{ top: style.top, height: rowHeight }}
      role="row"
      altRow={index % 2 === 1}
      onClick={onClick}
      className={activeIds && activeIds.includes(itemId) ? 'active' : undefined}
    >
      {allowSelection && (
        <TableCell
          key="checkbox"
          className="checkbox-cell"
          role="cell"
          style={{ width: checkboxColumnWidth }}
          onClick={selectRowClick}
        >
          <StyledCheckbox
            checked={selected}
            onChange={noop}
            title={selected ? 'Deselect' : 'Select'}
          />
        </TableCell>
      )}
      {item &&
        columns.map((column: Column) => (
          <TableColumn
            key={column.id}
            accessor={column.accessor as TableColumnProps['accessor']}
            width={column.width}
            item={item}
          />
        ))}
    </StyledTableRow>
  );
};

ItemRow.propTypes = {
  data: PropTypes.any,
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
};

export type TableWindowProps<
  Item extends { [Id in IdField]: IdValue } = any,
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
> = FlexProps & {
  columns: Column[];
  columnConfiguration?: ColumnConfig[];
  setColumnConfiguration?: (columns: ColumnConfig[]) => void;
  itemIds: IdArrayInput<IdValue>;
  itemLoader: ItemLoader<Item, IdField, IdValue>;
  selectedIds?: IdArrayInput<IdValue>;
  activeIds?: IdArrayInput<IdValue>;
  onSelectionChange?: (selectedIds: IdArrayInput<IdValue>) => void;
  onHeaderClick?: (column: Column) => void;
  onRowClick?: (item: Item, index: number) => void;
  orderBy?: OrderBy[];
  actionItems?: React.ReactNode;
  showActionBar?: boolean;
  csvFilename?: string;
  rightPane?: JSX.Element;
  rightPaneConfig?: RightPaneConfig;
  setRightPaneConfig?: (value: RightPaneConfig | null) => void;
  rightPaneIcon?: IconType;
};

/**
 *
 * @component TableWindow that displays virtualized list of rows
 * Assumes that it always has a checkbox selection as the first column
 */
const TableWindow: React.FC<TableWindowProps> = <
  Item extends { [Id in IdField]: IdValue },
  IdField extends string = 'id',
  IdValue extends string | number = Item[IdField],
>({
  columns,
  itemIds,
  itemLoader,
  selectedIds,
  activeIds,
  onSelectionChange,
  onHeaderClick,
  onRowClick,
  orderBy,
  columnConfiguration,
  setColumnConfiguration,
  actionItems,
  showActionBar = true,
  csvFilename,
  rightPane,
  rightPaneConfig,
  setRightPaneConfig,
  rightPaneIcon,
  ...props
}: TableWindowProps<Item, IdField, IdValue>) => {
  useEffect(() => {
    columns.forEach(assignColumnAccessor);
  }, [columns]);

  const scrollBarSize = useScrolBarSize();

  const allowSelection =
    !!onSelectionChange || (!!selectedIds && selectedIds.length > 0);

  const headerHeight = 36;
  const rowHeight = 32;
  const actionBarHeight = showActionBar && itemIds.length > 0 ? 48 : 0;
  const checkboxColumnWidth = 40;
  const borderWidth = 1;

  const totalColumnsWidth: number = useMemo(
    () =>
      columns.reduce<number>(
        (previousValue, currentValue) =>
          previousValue + (currentValue.width as number),
        allowSelection ? checkboxColumnWidth : 0
      ),
    [columns, allowSelection, checkboxColumnWidth]
  );
  const selectAllClick = useCallback(() => {
    if (onSelectionChange) {
      const allSelected = itemIds.length === selectedIds?.length;
      onSelectionChange(allSelected ? [] : itemIds);
    }
  }, [onSelectionChange, itemIds, selectedIds]);

  const [bodyWidth, setBodyWidth] = useState(totalColumnsWidth + scrollBarSize);

  const tableRef = useRef<HTMLDivElement>(null);
  const [width] = useSize(tableRef);

  const hasHorizontalScroll = tableRef.current
    ? tableRef.current.scrollWidth > width - borderWidth * 2
    : false;

  const updateWidth = useCallback(() => {
    if (tableRef.current) {
      const vScrollWidth =
        tableRef.current.offsetWidth -
        borderWidth * 2 +
        tableRef.current.scrollLeft;
      setBodyWidth(Math.min(totalColumnsWidth + scrollBarSize, vScrollWidth));
    }
  }, [totalColumnsWidth, scrollBarSize]);

  useEffect(updateWidth, [width, totalColumnsWidth]);

  useEffect(() => {
    tableRef.current?.addEventListener('scroll', updateWidth);
    return () => {
      tableRef.current?.removeEventListener('scroll', updateWidth);
    };
  }, [tableRef.current, updateWidth]);

  const [listRendered, setListRendered] = useState<boolean>(false);
  const onItemsRendered = useCallback(() => {
    setListRendered(true);
  }, []);
  const scrollListRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const activeId = activeIds?.[0];
    if (activeId && scrollListRef.current && listRendered) {
      const activeIndex = itemIds.indexOf(activeId);
      if (activeIndex === -1) {
        return;
      }
      const listElement = scrollListRef.current;
      const elementPosition = activeIndex * rowHeight;
      const listHeight = Math.max(listElement.offsetHeight, 300);
      const isAboveFold = elementPosition < listElement.scrollTop;
      const isBelowFold =
        elementPosition + rowHeight >= listElement.scrollTop + listHeight;
      if (isAboveFold) {
        listElement.scrollTop = Math.max(elementPosition, 0);
      } else if (isBelowFold) {
        listElement.scrollTop = Math.max(
          elementPosition - listHeight + rowHeight,
          0
        );
      }
    }
  }, [activeIds?.[0], scrollListRef.current, listRendered]);

  const hasRightPane =
    rightPane && rightPaneConfig?.visible && itemIds.length > 0;
  const maxTableWidth = totalColumnsWidth + scrollBarSize + borderWidth * 2;
  const maxTableHeight =
    itemIds.length * rowHeight +
    headerHeight +
    borderWidth * 2 +
    actionBarHeight +
    (hasHorizontalScroll ? scrollBarSize : 0);

  return (
    <Flex
      flexDirection="column"
      flexShrink={1}
      flexGrow={1}
      sx={{
        maxHeight: maxTableHeight,
      }}
      {...(props as any)}
    >
      <Flex
        flexDirection="row"
        sx={{
          flexGrow: 1,
          flexShrink: 1,
        }}
      >
        <Flex
          sx={{
            flexGrow: 1,
            flexShrink: 1,
            position: 'relative',
            zIndex: 5,
            width: '100%',
            '.table, .table-header-row': {
              borderTopRightRadius: hasRightPane ? 0 : undefined,
            },
            maxWidth: maxTableWidth,
          }}
        >
          <StyledTable
            ref={tableRef}
            className="table"
            style={{
              borderBottomLeftRadius:
                showActionBar && itemIds.length > 0 ? 0 : undefined,
              borderBottomRightRadius:
                showActionBar && itemIds.length > 0 ? 0 : undefined,
              borderBottom: showActionBar && itemIds.length > 0 ? 0 : undefined,
            }}
          >
            <AutoSizer>
              {({ height, width }) => (
                <>
                  <HeaderRow
                    headerHeight={headerHeight}
                    totalColumnsWidth={totalColumnsWidth}
                    scrollBarSize={scrollBarSize}
                    allowSelection={allowSelection}
                    selectAllClick={selectAllClick}
                    selectedIds={selectedIds}
                    itemIds={itemIds}
                    columns={columns}
                    onHeaderClick={onHeaderClick}
                    checkboxColumnWidth={checkboxColumnWidth}
                    orderBy={orderBy}
                    columnConfiguration={columnConfiguration}
                    setColumnConfiguration={setColumnConfiguration}
                  />
                  <FixedSizeList
                    outerRef={scrollListRef}
                    onItemsRendered={onItemsRendered}
                    className="tbody"
                    height={
                      height -
                      headerHeight -
                      borderWidth -
                      (hasHorizontalScroll ? scrollBarSize : 0)
                    }
                    width={bodyWidth}
                    itemCount={itemIds.length}
                    itemSize={rowHeight}
                    itemData={{
                      itemIds,
                      itemLoader,
                      columns,
                      selectedIds,
                      activeIds,
                      onRowClick,
                      onSelectionChange,
                      rowHeight,
                      allowSelection,
                      checkboxColumnWidth,
                    }}
                  >
                    {ItemRow}
                  </FixedSizeList>
                </>
              )}
            </AutoSizer>
          </StyledTable>
          {itemIds.length > 0 &&
            columnConfiguration &&
            setColumnConfiguration &&
            columnConfiguration.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  zIndex: 5,
                  top: '5px',
                  right: '4px',
                }}
              >
                <ColumnSelector
                  columnConfiguration={columnConfiguration}
                  setColumnConfiguration={setColumnConfiguration}
                />
              </Box>
            )}
        </Flex>
        {hasRightPane && (
          <Flex
            sx={{
              boxSizing: 'border-box',
              borderTopRightRadius: 'default',
              borderWidth: hasRightPane ? '1px' : 0,
              borderStyle: 'solid',
              borderColor: 'tableBorder',
              flexGrow: 1,
              flexShrink: 1,
              width: '100%',
              // transition: 'all .3s ease-in-out',
              overflow: 'hidden',
              maxWidth: hasRightPane ? rightPaneConfig.width : '0px',
            }}
          >
            {rightPane}
          </Flex>
        )}
      </Flex>
      {showActionBar && itemIds.length > 0 && (
        <TableActionBar
          p={2}
          className="table-actions"
          bg="tableActions"
          sx={{
            // transition: 'all .3s ease-in-out',
            backfaceVisibility: 'hidden',
            borderBottomRightRadius: 'default',
            borderBottomLeftRadius: 'default',
            maxWidth: hasRightPane
              ? maxTableWidth + (rightPaneConfig?.width ?? 0)
              : maxTableWidth,
          }}
          columns={columns}
          selectedIds={selectedIds}
          numItems={itemIds.length}
          itemLoader={itemLoader}
          rightPaneConfig={rightPaneConfig}
          setRightPaneConfig={setRightPaneConfig}
          rightPaneIcon={rightPaneIcon}
          csvFilename={csvFilename ?? `${itemLoader.queryName}.csv`}
        >
          {itemIds.length > 0 && actionItems}
        </TableActionBar>
      )}
    </Flex>
  );
};

export default TableWindow;
